import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DualViewSwitcher } from "../components/DualViewSwitcher";
import TapTrackLogo from "../components/TapTrackLogo";

const DAYS_OF_WEEK = [
  { id: 0, label: "Monday", short: "Mon" },
  { id: 1, label: "Tuesday", short: "Tue" },
  { id: 2, label: "Wednesday", short: "Wed" },
  { id: 3, label: "Thursday", short: "Thu" },
  { id: 4, label: "Friday", short: "Fri" },
  { id: 5, label: "Saturday", short: "Sat" },
  { id: 6, label: "Sunday", short: "Sun" },
];

const getCalendarDays = (monthOffset) => {
  const now = new Date();
  const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(year, month, d).toISOString().split("T")[0]);
  }
  return { days, label: targetDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" }) };
};

export default function MySchedule() {
  const navigate = useNavigate();
  const [myAvailability, setMyAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [weekPattern, setWeekPattern] = useState({});
  const [applyingPattern, setApplyingPattern] = useState(false);

  const loadMyAvailability = async () => {
    setLoadingAvailability(true);
    try {
      const res = await apiClient.get("/availability");
      setMyAvailability(res.data || []);
    } catch (e) {
      // non-fatal
    } finally {
      setLoadingAvailability(false);
    }
  };

  useEffect(() => { loadMyAvailability(); /* eslint-disable-next-line */ }, []);

  const toggleAvailability = async (dateStr) => {
    const existing = myAvailability.find(a => a.date === dateStr);
    const newAvail = !existing?.available;
    try {
      await apiClient.put("/availability", { date: dateStr, available: newAvail });
      setMyAvailability(prev => {
        const filtered = prev.filter(a => a.date !== dateStr);
        return [...filtered, { date: dateStr, available: newAvail }];
      });
    } catch (e) {
      toast.error("Failed to update availability");
    }
  };

  const applyWeekPattern = async () => {
    const activeDays = Object.entries(weekPattern).filter(([, v]) => v !== undefined);
    if (activeDays.length === 0) {
      toast.error("Set at least one day in the pattern");
      return;
    }
    setApplyingPattern(true);
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      const dates = [];
      for (let offset = 0; offset < 3; offset++) {
        const cal = getCalendarDays(offset);
        cal.days.forEach(dateStr => {
          if (dateStr < todayStr) return;
          const dow = (new Date(dateStr).getDay() + 6) % 7;
          const patternValue = weekPattern[dow];
          if (patternValue !== undefined) dates.push({ date: dateStr, available: patternValue });
        });
      }
      if (dates.length === 0) {
        toast.error("No future dates match the pattern");
        setApplyingPattern(false);
        return;
      }
      const res = await apiClient.post("/availability/batch", { dates });
      toast.success(`Pattern applied to ${res.data.updated} days`);
      loadMyAvailability();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply pattern");
    } finally {
      setApplyingPattern(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-schedule-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="schedule-back-btn" className="h-9 px-2">
            <ArrowLeft className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Back</span>
          </Button>
          <TapTrackLogo className="hidden sm:block h-8 w-auto" />
          <div className="flex-1">
            <h1 className="font-heading font-bold text-lg sm:text-xl text-slate-900">My Schedule</h1>
            <p className="text-xs text-slate-500">Set your availability — your manager uses this to plan shifts</p>
          </div>
          <DualViewSwitcher />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h3 className="font-heading font-bold text-slate-900">My Availability</h3>
            </div>
            {loadingAvailability ? (
              <div className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : (
              <div className="space-y-4">
                {/* Week Pattern Bar */}
                <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4" data-testid="week-pattern-bar">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Week Pattern</p>
                      <p className="text-xs text-slate-500">Set a weekly pattern then apply to all future dates</p>
                    </div>
                    <button
                      onClick={applyWeekPattern}
                      disabled={applyingPattern || Object.values(weekPattern).every(v => v === undefined)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        applyingPattern || Object.values(weekPattern).every(v => v === undefined)
                          ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                      data-testid="apply-pattern-btn"
                    >
                      {applyingPattern ? "Applying..." : "Apply to Quarter"}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map(day => {
                      const val = weekPattern[day.id];
                      return (
                        <button
                          key={day.id}
                          onClick={() => setWeekPattern(prev => ({
                            ...prev,
                            [day.id]: prev[day.id] === true ? false : prev[day.id] === false ? undefined : true
                          }))}
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors border ${
                            val === true ? "bg-emerald-500 text-white border-emerald-600" :
                            val === false ? "bg-red-200 text-red-700 border-red-300" :
                            "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                          }`}
                          data-testid={`pattern-day-${day.id}`}
                        >
                          {day.short}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded" /> Available</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-200 rounded" /> Unavailable</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-white border border-slate-200 rounded" /> No change</span>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="availability-calendar">
                  {[0, 1, 2].map(offset => {
                    const cal = getCalendarDays(offset);
                    const availMap = {};
                    myAvailability.forEach(a => { availMap[a.date] = a.available; });
                    return (
                      <div key={offset} className="border border-slate-200 rounded-xl p-4">
                        <h4 className="font-semibold text-slate-800 text-center mb-3">{cal.label}</h4>
                        <div className="grid grid-cols-7 gap-1 text-center text-xs">
                          {["Mo","Tu","We","Th","Fr","Sa","Su"].map(d => <div key={d} className="font-medium text-slate-400 pb-1">{d}</div>)}
                          {(() => {
                            const firstDay = new Date(cal.days[0]);
                            const startPad = (firstDay.getDay() + 6) % 7;
                            const cells = [];
                            for (let i = 0; i < startPad; i++) cells.push(<div key={`pad-${i}`} />);
                            cal.days.forEach(dateStr => {
                              const isAvail = availMap[dateStr];
                              const isPast = dateStr < new Date().toISOString().split("T")[0];
                              cells.push(
                                <button
                                  key={dateStr}
                                  onClick={() => !isPast && toggleAvailability(dateStr)}
                                  disabled={isPast}
                                  className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                                    isPast ? "text-slate-300 cursor-not-allowed" :
                                    isAvail === true ? "bg-emerald-500 text-white" :
                                    isAvail === false ? "bg-red-200 text-red-700" :
                                    "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                  }`}
                                  data-testid={`avail-${dateStr}`}
                                >
                                  {parseInt(dateStr.split("-")[2])}
                                </button>
                              );
                            });
                            return cells;
                          })()}
                        </div>
                        <div className="flex justify-center gap-4 mt-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded" /> Available</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-200 rounded" /> Unavailable</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
